import React, { useState, useEffect, useCallback, useRef } from 'react';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import PersonalInfoSection from './components/PersonalInfoSection';
import PaySettingsSection from './components/PaySettingsSection';
import DisplayPreferencesSection from './components/DisplayPreferencesSection';
import DataManagementSection from './components/DataManagementSection';
import OperationalPreferencesSection from './components/OperationalPreferencesSection';
import IntegrationsSection from './components/IntegrationsSection';
import PrivacySecuritySection from './components/PrivacySecuritySection';
import AdvancedSection from './components/AdvancedSection';
import AboutSupportSection from './components/AboutSupportSection';
import SaveActionsBar from './components/SaveActionsBar';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
// DISABLED - Tables not yet created in Supabase
// import CalendarSyncSection from './components/CalendarSyncSection';
// import FlightTrackingSection from './components/FlightTrackingSection';
import NotificationControlsSection from './components/NotificationControlsSection';
import NotificationPresets from './components/NotificationPresets';
import Icon from '../../components/AppIcon';

const Settings = () => {
  const { user } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [customPresets, setCustomPresets] = useState([]);
  const [activePreset, setActivePreset] = useState('custom');
  const saveTimeoutRef = useRef(null);
  const isInitialLoad = useRef(true);

  const initialFormData = {
    // Personal Info
    fullName: "",
    employeeId: "",
    baseLocation: "",
    aircraftType: "",
    rank: "",
    hireDate: "",
    homeAirport: "",
    // Pay Settings
    contractYear: "",
    hourlyRate: "",
    minimumGuarantee: "",
    overrideRate: "",
    perDiemType: "",
    perDiemRate: "",
    premiumMultiplier: "",
    nightDifferential: "",
    sickTimeAccrued: "",
    // Display Preferences
    emailNotifications: true,
    pushNotifications: true,
    weeklySummary: false,
    refreshInterval: "15",
    autoSync: true,
    dateFormat: "MM/DD/YYYY",
    timeFormat: "12h",
    showAnimations: true,
    compactView: false,
    theme: "dark",
    timezoneDisplay: "local",
    // Notification Controls
    notifyFlightDelays: true,
    notifyGateChanges: true,
    notifyScheduleChanges: true,
    notifyCompliance: true,
    notifyPayDiscrepancies: true,
    notifyWeatherAlerts: false,
    enableQuietHours: false,
    quietHoursStart: "22:00",
    quietHoursEnd: "07:00",
    quietHoursCriticalOverride: true,
    notificationFrequency: "realtime",
    delayAlertThreshold: "15",
    groupSimilarAlerts: true,
    enableSmsAlerts: false,
    smsPhoneNumber: "",
    smsCriticalThreshold: "60",
    smsBackupOnly: false,
    enablePushNotifications: true,
    notificationEmail: "",
    // Operational Preferences
    minimumConnectionTime: "45",
    preferredRestPeriod: "12",
    isCommuter: false,
    maxCommuteTime: "3",
    commuteBuffer: "90",
    preferredAirlines: [],
    backupFlightAlerts: true,
    alertFdp80: true,
    alertFdp90: true,
    alertFar117Violation: true,
    alertRestViolation: true,
    alertScheduleChange: true,
    // Integrations
    calendarProvider: "",
    calendarIncludeCommute: true,
    calendarIncludeRest: false,
    flightAwareApiKey: "",
    // Privacy & Security
    enable2FA: false,
    twoFactorMethod: "authenticator",
    enableBiometric: false,
    sessionTimeout: "1440",
    // Advanced
    enableBetaFeatures: false,
    betaAiSchedule: false,
    betaPayProjections: false,
    betaCrewTrading: false,
    betaAnalytics: false,
    enableDeveloperMode: false,
    experimentalUi: false,
    // Notification Presets
    notificationPresets: [],
    activeNotificationPreset: 'custom'
  };

  const [formData, setFormData] = useState(initialFormData);
  const [originalData, setOriginalData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [payRates, setPayRates] = useState(null);  // Auto-looked up pay rates

  // Load settings from Supabase
  useEffect(() => {
    const loadSettings = async () => {
      if (!user?.id) {
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      try {
        // First get user_profile to get employee_id
        const { data: profile } = await supabase
          ?.from('user_profiles')
          ?.select('*')
          ?.eq('id', user?.id)
          ?.single();

        // Then get user_settings using user_id (auth UUID)
        const { data, error } = await supabase
          ?.from('user_settings')
          ?.select('*')
          ?.eq('user_id', user?.id)
          ?.single();

        if (error && error?.code !== 'PGRST116') {
          console.error('Error loading settings:', error);
        }
        
        // Merge profile and settings data
        const loadedData = {
          ...initialFormData,
          fullName: data?.employee_name || profile?.employee_name || "",
          employeeId: data?.employee_id || profile?.employee_id || "",
          baseLocation: data?.home_base || "",
          aircraftType: data?.aircraft_type_group || data?.default_aircraft_model || "",
          rank: data?.position || "",
          hireDate: data?.date_of_hire || "",
          homeAirport: data?.home_airport || "",
          sickTimeAccrued: data?.sick_time_accrued || "0",
          theme: data?.theme || "dark",
          timezoneDisplay: data?.timezone_display || "local",
          ...(data?.preferences || {})
        };
        setFormData(loadedData);
        setOriginalData(loadedData);
        
        // Load custom presets
        if (data?.preferences?.notificationPresets) {
          setCustomPresets(data?.preferences?.notificationPresets);
        }
        
        // Set active preset
        if (data?.preferences?.activeNotificationPreset) {
          setActivePreset(data?.preferences?.activeNotificationPreset);
        }

        // Auto-lookup pay rates from pay_scales
        if (data?.position && data?.default_aircraft_model && data?.date_of_hire) {
          await loadPayRates(data.position, data.default_aircraft_model, data.date_of_hire);
        }
      } catch (error) {
        console.error('Error loading settings:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, [user]);

  // Function to load pay rates from pay_scales table
  const loadPayRates = async (position, aircraftModel, hireDate) => {
    try {
      // Calculate years of service
      const hire = new Date(hireDate);
      const now = new Date();
      const yearsOfService = Math.floor((now - hire) / (365.25 * 24 * 60 * 60 * 1000)) + 1;
      
      // Map position to pay_scales format
      const positionMap = { 
        'CA': 'Captain', 
        'Captain': 'Captain', 
        'A': 'Captain',
        'FO': 'First Officer', 
        'First Officer': 'First Officer',
        'B': 'First Officer'
      };
      const payPosition = positionMap[position] || position;
      
      // Map aircraft codes to full names in pay_scales
      const aircraftMap = {
        '73N': 'B-737-900',
        '737-900': 'B-737-900',
        'B737-900': 'B-737-900',
        '76I': 'B-767-400',
        '767-400': 'B-767-400',
        'B767-400': 'B-767-400',
        '777': 'B-777',
        'B777': 'B-777',
        '787': 'B-787',
        'B787': 'B-787'
      };
      const mappedAircraft = aircraftMap[aircraftModel] || aircraftModel;
      
      console.log('🔍 Looking up pay rate:', { payPosition, mappedAircraft, yearsOfService });
      
      // Get current effective pay rate
      const { data: rates, error } = await supabase
        ?.from('pay_scales')
        ?.select('*')
        ?.eq('position', payPosition)
        ?.eq('aircraft_model', mappedAircraft)
        ?.eq('pay_year', Math.min(yearsOfService, 12))  // Cap at year 12
        ?.order('effective_date', { ascending: false })
        ?.limit(1);
      
      console.log('💰 Pay scales query result:', rates, error);
      
      if (!error && rates?.length > 0) {
        setPayRates({
          hourlyRate: parseFloat(rates[0].hourly_rate),
          effectiveDate: rates[0].effective_date,
          payYear: rates[0].pay_year,
          source: 'pay_scales'
        });
      } else {
        console.warn('⚠️ No pay rate found for:', { payPosition, mappedAircraft, yearsOfService });
        setPayRates(null);
      }
    } catch (error) {
      console.error('Error loading pay rates:', error);
      setPayRates(null);
    }
  };

  // Track changes - with defensive JSON stringify
  useEffect(() => {
    try {
      // Use a simpler comparison to avoid stringify issues
      const formKeys = Object.keys(formData || {});
      const origKeys = Object.keys(originalData || {});
      
      if (formKeys.length !== origKeys.length) {
        setHasChanges(true);
        return;
      }
      
      const hasChange = formKeys.some(key => {
        const formVal = formData?.[key];
        const origVal = originalData?.[key];
        // Skip complex objects
        if (typeof formVal === 'object' && formVal !== null) {
          return JSON.stringify(formVal) !== JSON.stringify(origVal);
        }
        return formVal !== origVal;
      });
      
      setHasChanges(hasChange);
    } catch (error) {
      // Handle any errors gracefully
      console.warn('Unable to compare form data:', error);
      setHasChanges(true);
    }
  }, [formData, originalData]);

  const handleInputChange = (e) => {
    if (!e?.target) return;
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleCheckboxChange = (name, checked) => {
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.fullName?.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData?.baseLocation) {
      newErrors.baseLocation = 'Base location is required';
    }

    if (!formData?.aircraftType) {
      newErrors.aircraftType = 'Aircraft type is required';
    }

    if (!formData?.rank) {
      newErrors.rank = 'Rank is required';
    }

    if (!formData?.hireDate) {
      newErrors.hireDate = 'Hire date is required';
    }

    if (formData?.notificationEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.notificationEmail)) {
      newErrors.notificationEmail = 'Valid email address is required';
    }

    if (formData?.enableSmsAlerts && !formData?.smsPhoneNumber?.trim()) {
      newErrors.smsPhoneNumber = 'Phone number is required for SMS alerts';
    }

    if (formData?.enableSmsAlerts && formData?.smsPhoneNumber && !/^\+?[1-9]\d{1,14}$/?.test(formData?.smsPhoneNumber?.replace(/[\s()-]/g, ''))) {
      newErrors.smsPhoneNumber = 'Valid phone number is required (E.164 format)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    if (!user?.id) {
      console.error('No user ID available');
      setSaveError('No user ID available');
      return;
    }

    setIsSaving(true);
    setSaveSuccess(false);
    setSaveError(null);

    try {
      const { employeeId, fullName, baseLocation, aircraftType, rank, hireDate, sickTimeAccrued, theme, timezoneDisplay, homeAirport, ...preferences } = formData;

      // Include custom presets and active preset in preferences
      const updatedPreferences = {
        ...preferences,
        notificationPresets: customPresets,
        activeNotificationPreset: activePreset
      };

      // Check if record exists by user_id
      const { data: existing } = await supabase
        ?.from('user_settings')
        ?.select('employee_id')
        ?.eq('user_id', user?.id)
        ?.single();

      const settingsData = {
        user_id: user?.id,
        employee_id: employeeId || existing?.employee_id || `temp_${user.id.substring(0, 8)}`,
        employee_name: fullName || null,
        position: rank || null,
        date_of_hire: hireDate || null,
        home_base: baseLocation || null,
        home_airport: homeAirport || null,
        aircraft_type_group: aircraftType || null,
        aircraft_category: rank === 'Captain' ? 'CA' : (rank === 'First Officer' ? 'FO' : null),
        default_aircraft_model: aircraftType || null,
        sick_time_accrued: parseFloat(sickTimeAccrued) || 0,
        theme: theme || 'dark',
        timezone_display: timezoneDisplay || 'local',
        preferences: updatedPreferences,
        updated_at: new Date()?.toISOString()
      };

      let settingsError;
      if (existing) {
        // Update existing record
        const result = await supabase
          ?.from('user_settings')
          ?.update(settingsData)
          ?.eq('user_id', user?.id);
        settingsError = result?.error;
      } else {
        // Insert new record
        settingsData.created_at = new Date()?.toISOString();
        const result = await supabase
          ?.from('user_settings')
          ?.insert(settingsData);
        settingsError = result?.error;
      }

      if (settingsError) {
        console.error('Error saving settings:', settingsError);
        setSaveError(settingsError.message || 'Failed to save settings');
        return;
      }

      // Also update user_profiles
      const { error: profileError } = await supabase?.from('user_profiles')?.update({
          employee_name: fullName,
          full_name: fullName,
          updated_at: new Date()?.toISOString()
        })?.eq('id', user?.id);

      if (profileError) {
        console.warn('Error updating profile:', profileError);
      }

      // Reload pay rates if position/aircraft changed
      if (rank && aircraftType && hireDate) {
        await loadPayRates(rank, aircraftType, hireDate);
      }

      setOriginalData(formData);
      setSaveSuccess(true);
      setIsEditing(false);
      setHasChanges(false);

      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Failed to save settings:', error);
      setSaveError(error?.message || 'An unexpected error occurred');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setFormData(originalData);
    setErrors({});
    setIsEditing(false);
    setHasChanges(false);
  };

  const handleExport = () => {
    const dataStr = JSON.stringify(formData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `winglet-settings-${new Date()?.toISOString()?.split('T')?.[0]}.json`;
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleImport = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importedData = JSON.parse(e?.target?.result);
        setFormData(prev => ({
          ...prev,
          ...importedData,
          employeeId: prev?.employeeId
        }));
      } catch (error) {
        console.error('Failed to import settings:', error);
      }
    };
    reader?.readAsText(file);
  };

  // Preset management functions
  const handleApplyPreset = (presetConfig) => {
    setFormData(prev => ({
      ...prev,
      ...presetConfig
    }));
    setActivePreset('custom');
  };

  const handlePresetChange = (presetId) => {
    setActivePreset(presetId);
    
    // Find and apply the preset configuration
    const presetConfigs = {
      'home-trips': {
        notifyFlightDelays: true,
        notifyGateChanges: true,
        notifyScheduleChanges: true,
        notifyCompliance: true,
        notifyPayDiscrepancies: true,
        notifyWeatherAlerts: false,
        enableQuietHours: true,
        quietHoursStart: '22:00',
        quietHoursEnd: '07:00',
        quietHoursCriticalOverride: true,
        notificationFrequency: 'realtime',
        delayAlertThreshold: '30',
        groupSimilarAlerts: true,
        enableSmsAlerts: false,
        smsCriticalThreshold: '60',
        smsBackupOnly: false,
        enablePushNotifications: true
      },
      'international': {
        notifyFlightDelays: true,
        notifyGateChanges: true,
        notifyScheduleChanges: true,
        notifyCompliance: true,
        notifyPayDiscrepancies: true,
        notifyWeatherAlerts: true,
        enableQuietHours: false,
        quietHoursStart: '22:00',
        quietHoursEnd: '07:00',
        quietHoursCriticalOverride: true,
        notificationFrequency: 'realtime',
        delayAlertThreshold: '15',
        groupSimilarAlerts: false,
        enableSmsAlerts: true,
        smsCriticalThreshold: '60',
        smsBackupOnly: false,
        enablePushNotifications: true
      },
      'tight-connections': {
        notifyFlightDelays: true,
        notifyGateChanges: true,
        notifyScheduleChanges: true,
        notifyCompliance: true,
        notifyPayDiscrepancies: true,
        notifyWeatherAlerts: true,
        enableQuietHours: false,
        quietHoursStart: '22:00',
        quietHoursEnd: '07:00',
        quietHoursCriticalOverride: true,
        notificationFrequency: 'realtime',
        delayAlertThreshold: '0',
        groupSimilarAlerts: false,
        enableSmsAlerts: true,
        smsCriticalThreshold: '30',
        smsBackupOnly: false,
        enablePushNotifications: true
      }
    };

    if (presetId !== 'custom' && presetConfigs?.[presetId]) {
      setFormData(prev => ({
        ...prev,
        ...presetConfigs?.[presetId]
      }));
    }
  };

  const handleSaveCustomPreset = (newPreset) => {
    setCustomPresets(prev => [...prev, newPreset]);
    setHasChanges(true);
  };

  const handleDeleteCustomPreset = (presetId) => {
    setCustomPresets(prev => prev?.filter(p => p?.id !== presetId));
    if (activePreset === presetId) {
      setActivePreset('custom');
    }
    setHasChanges(true);
  };

  // Extract notification config for presets
  const getCurrentNotificationConfig = () => ({
    notifyFlightDelays: formData?.notifyFlightDelays,
    notifyGateChanges: formData?.notifyGateChanges,
    notifyScheduleChanges: formData?.notifyScheduleChanges,
    notifyCompliance: formData?.notifyCompliance,
    notifyPayDiscrepancies: formData?.notifyPayDiscrepancies,
    notifyWeatherAlerts: formData?.notifyWeatherAlerts,
    enableQuietHours: formData?.enableQuietHours,
    quietHoursStart: formData?.quietHoursStart,
    quietHoursEnd: formData?.quietHoursEnd,
    quietHoursCriticalOverride: formData?.quietHoursCriticalOverride,
    notificationFrequency: formData?.notificationFrequency,
    delayAlertThreshold: formData?.delayAlertThreshold,
    groupSimilarAlerts: formData?.groupSimilarAlerts,
    enableSmsAlerts: formData?.enableSmsAlerts,
    smsPhoneNumber: formData?.smsPhoneNumber,
    smsCriticalThreshold: formData?.smsCriticalThreshold,
    smsBackupOnly: formData?.smsBackupOnly,
    enablePushNotifications: formData?.enablePushNotifications,
    notificationEmail: formData?.notificationEmail,
    emailNotifications: formData?.emailNotifications
  });

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/dashboard', icon: 'Home' },
    { label: 'Settings', icon: 'Settings' }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Sidebar />
        <main className="flex-1 lg:ml-60 pb-16 lg:pb-0">
          <div className="p-4 md:p-6 lg:p-8 space-y-6">
            <Breadcrumb items={[
              { label: 'Dashboard', href: '/dashboard' },
              { label: 'Settings', href: '/settings' }
            ]} />

            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-foreground">Settings</h1>
                <p className="text-sm md:text-base text-muted-foreground mt-1 font-caption">
                  Manage your profile, preferences, and integrations
                </p>
              </div>
            </div>

            {/* Error Display */}
            {saveError && (
              <div className="mb-4 p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                <div className="flex items-center gap-3">
                  <Icon name="AlertCircle" size={20} className="text-red-500" />
                  <div>
                    <p className="text-sm font-medium text-red-500">Save Failed</p>
                    <p className="text-xs text-muted-foreground">{saveError}</p>
                  </div>
                  <button 
                    onClick={() => setSaveError(null)} 
                    className="ml-auto text-muted-foreground hover:text-foreground"
                  >
                    <Icon name="X" size={16} />
                  </button>
                </div>
              </div>
            )}

            {/* Success Display */}
            {saveSuccess && (
              <div className="mb-4 p-4 rounded-lg bg-accent-green/10 border border-accent-green/20">
                <div className="flex items-center gap-3">
                  <Icon name="CheckCircle" size={20} className="text-accent-green" />
                  <p className="text-sm font-medium text-accent-green">Settings saved successfully!</p>
                </div>
              </div>
            )}

            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-blue"></div>
              </div>
            ) : (
              <div className="space-y-6">
                <PersonalInfoSection 
                  formData={formData}
                  handleInputChange={handleInputChange}
                  errors={errors}
                  isEditing={isEditing}
                  setIsEditing={setIsEditing}
                  onSave={handleSave}
                  isSaving={isSaving}
                />
                <PaySettingsSection 
                  formData={formData}
                  handleInputChange={handleInputChange}
                  errors={errors}
                  isEditing={isEditing}
                  setIsEditing={setIsEditing}
                  payRates={payRates}
                  onSave={handleSave}
                  isSaving={isSaving}
                />
                <DisplayPreferencesSection 
                  formData={formData}
                  handleInputChange={handleInputChange}
                  handleCheckboxChange={handleCheckboxChange}
                />
                <OperationalPreferencesSection 
                  formData={formData}
                  handleInputChange={handleInputChange}
                  handleCheckboxChange={handleCheckboxChange}
                />
                <NotificationPresets
                  currentConfig={getCurrentNotificationConfig()}
                  onApplyPreset={handleApplyPreset}
                  customPresets={customPresets}
                  onSaveCustomPreset={handleSaveCustomPreset}
                  onDeleteCustomPreset={handleDeleteCustomPreset}
                />
                <NotificationControlsSection 
                  formData={formData}
                  handleInputChange={handleInputChange}
                  handleCheckboxChange={handleCheckboxChange}
                  errors={errors}
                  activePreset={activePreset}
                  onPresetChange={handlePresetChange}
                />
                <IntegrationsSection 
                  formData={formData}
                  handleInputChange={handleInputChange}
                  handleCheckboxChange={handleCheckboxChange}
                  errors={errors}
                />
                {/* DISABLED - Tables not yet created in Supabase */}
                {/* <CalendarSyncSection /> */}
                {/* <FlightTrackingSection /> */}
                <PrivacySecuritySection 
                  formData={formData}
                  handleInputChange={handleInputChange}
                  handleCheckboxChange={handleCheckboxChange}
                />
                <DataManagementSection />
                <AdvancedSection 
                  formData={formData}
                  handleCheckboxChange={handleCheckboxChange}
                />
                <AboutSupportSection />
              </div>
            )}
          </div>

          {hasChanges && (
            <SaveActionsBar 
              onSave={handleSave}
              onReset={handleReset}
              isSaving={isSaving}
              saveSuccess={saveSuccess}
            />
          )}
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      <main className="flex-1 lg:ml-60 pb-16 lg:pb-0">
        <div className="p-4 md:p-6 lg:p-8 space-y-6">
          <Breadcrumb items={[
            { label: 'Dashboard', href: '/dashboard' },
            { label: 'Settings', href: '/settings' }
          ]} />

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-foreground">Settings</h1>
              <p className="text-sm md:text-base text-muted-foreground mt-1 font-caption">
                Manage your profile, preferences, and integrations
              </p>
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-blue"></div>
            </div>
          ) : (
            <div className="space-y-6">
              <PersonalInfoSection 
                formData={formData}
                handleInputChange={handleInputChange}
                errors={errors}
                isEditing={isEditing}
                setIsEditing={setIsEditing}
                onSave={handleSave}
                isSaving={isSaving}
              />
              <PaySettingsSection 
                formData={formData}
                handleInputChange={handleInputChange}
                errors={errors}
                isEditing={isEditing}
                setIsEditing={setIsEditing}
                payRates={payRates}
                onSave={handleSave}
                isSaving={isSaving}
              />
              <DisplayPreferencesSection 
                formData={formData}
                handleInputChange={handleInputChange}
                handleCheckboxChange={handleCheckboxChange}
              />
              <OperationalPreferencesSection 
                formData={formData}
                handleInputChange={handleInputChange}
                handleCheckboxChange={handleCheckboxChange}
              />
              <NotificationPresets
                currentConfig={getCurrentNotificationConfig()}
                onApplyPreset={handleApplyPreset}
                customPresets={customPresets}
                onSaveCustomPreset={handleSaveCustomPreset}
                onDeleteCustomPreset={handleDeleteCustomPreset}
              />
              <NotificationControlsSection 
                formData={formData}
                handleInputChange={handleInputChange}
                handleCheckboxChange={handleCheckboxChange}
                errors={errors}
                activePreset={activePreset}
                onPresetChange={handlePresetChange}
              />
              <IntegrationsSection 
                formData={formData}
                handleInputChange={handleInputChange}
                handleCheckboxChange={handleCheckboxChange}
                errors={errors}
              />
              {/* DISABLED - Tables not yet created in Supabase */}
              {/* <CalendarSyncSection /> */}
              {/* <FlightTrackingSection /> */}
              <PrivacySecuritySection 
                formData={formData}
                handleInputChange={handleInputChange}
                handleCheckboxChange={handleCheckboxChange}
              />
              <DataManagementSection />
              <AdvancedSection 
                formData={formData}
                handleCheckboxChange={handleCheckboxChange}
              />
              <AboutSupportSection />
            </div>
          )}
        </div>

        {hasChanges && (
          <SaveActionsBar 
            onSave={handleSave}
            onReset={handleReset}
            isSaving={isSaving}
            saveSuccess={saveSuccess}
          />
        )}
      </main>
    </div>
  );
};

export default Settings;